import React from "react";
import { apiClient } from "@/api/apiClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, X as XLogIcon } from "lucide-react";
import { toast } from "sonner";
import AccountManagement from "../components/profile/AccountManagement";
import SkeletonProfileHeader from "../components/ui/SkeletonProfileHeader";

// Icons
function PencilIcon(props) {
    return (
        <svg
            {...props}
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
            <path d="m15 5 4 4" />
        </svg>
    )
}

export default function ProfilPage() {
    const { data: user, isLoading } = useQuery({
        queryKey: ['user'],
        queryFn: () => apiClient.auth.me(),
    });
    const queryClient = useQueryClient();

    // Name Editing State
    const [isEditingName, setIsEditingName] = React.useState(false);
    const [newName, setNewName] = React.useState("");

    React.useEffect(() => {
        if (user?.brand_name) setNewName(user.brand_name);
    }, [user]);

    const updateNameMutation = useMutation({
        mutationFn: async (name) => {
            return apiClient.auth.updateProfile({ brand_name: name });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['user']);
            toast.success("Nom d'utilisateur mis à jour");
            setIsEditingName(false);
        },
        onError: () => {
            toast.error("Erreur lors de la mise à jour");
        }
    });

    const handleSaveName = () => {
        if (newName.trim()) {
            updateNameMutation.mutate(newName);
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen p-6 md:p-8" aria-busy="true" aria-label="Chargement du profil">
                <div className="max-w-4xl mx-auto">
                    <SkeletonProfileHeader />
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen p-6 md:p-8">
            <div className="max-w-4xl mx-auto">
                {/* Profile Header */}
                <div className="relative mb-8 group">
                    {/* Background Blur Mesh */}
                    <div className="absolute inset-0 bg-gradient-to-r from-amber-600 via-yellow-600 to-orange-500 opacity-80 blur-3xl rounded-3xl -z-10 transform scale-95 md:scale-100 transition-all duration-700 group-hover:blur-[80px] group-hover:scale-105" />

                    <Card className="border-0 bg-white/80 dark:bg-[hsl(225,14%,10%)]/80 backdrop-blur-xl shadow-2xl overflow-hidden rounded-3xl ring-1 ring-white/50 dark:ring-white/10">
                        {/* Cover Image Area with More Images */}
                        <div className="h-48 relative overflow-hidden">
                            <img
                                src="/profile-banner.png"
                                alt="Bannière Profil"
                                className="w-full h-full object-cover"
                            />
                            {/* Overlay Gradient */}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                        </div>

                        <CardContent className="relative px-8 pb-8">
                            <div className="flex flex-col md:flex-row items-center md:items-start gap-6 -mt-16">
                                {/* Avatar */}
                                <div className="relative group/avatar">
                                    <div className="w-32 h-32 rounded-full border-4 border-white dark:border-[hsl(225,14%,14%)] shadow-2xl bg-white dark:bg-[hsl(225,14%,14%)] flex items-center justify-center overflow-hidden transform transition-transform duration-300 group-hover/avatar:scale-105 group-hover/avatar:rotate-2">
                                        {/* Avatar Gradient */}
                                        <div className="w-full h-full bg-gradient-to-br from-amber-600 to-amber-700 flex items-center justify-center">
                                            <span className="text-4xl font-black text-white drop-shadow-md">
                                                {user?.brand_name?.charAt(0) || 'U'}
                                            </span>
                                        </div>
                                    </div>
                                    {/* Status Indicator (Always Green as requested) */}
                                    <div className="absolute bottom-2 right-2 w-6 h-6 rounded-full border-4 border-white dark:border-[hsl(225,14%,10%)] shadow-md bg-green-500" />
                                </div>

                                {/* Text Info & Edit Name */}
                                <div className="text-center md:text-left pb-2 flex-1 w-full md:w-auto md:mt-24">
                                    {isEditingName ? (
                                        <div className="flex items-center justify-center md:justify-start gap-2 mb-1">
                                            <input
                                                className="text-3xl md:text-4xl font-extrabold text-gray-900 dark:text-white tracking-tight bg-transparent border-b-2 border-amber-600 focus:outline-none w-full max-w-[300px]"
                                                value={newName}
                                                onChange={(e) => setNewName(e.target.value)}
                                                autoFocus
                                            />
                                            <Button size="icon" className="h-8 w-8 bg-green-500 hover:bg-green-600 rounded-full" onClick={handleSaveName}>
                                                <Check className="w-4 h-4 text-white" />
                                            </Button>
                                            <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full" onClick={() => setIsEditingName(false)}>
                                                <XLogIcon className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    ) : (
                                        <div
                                            className={`flex items-center justify-center md:justify-start gap-2 group/name ${user?.id ? 'cursor-pointer' : ''}`}
                                            onClick={() => user?.id && setIsEditingName(true)}
                                        >
                                            <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 dark:text-white tracking-tight mb-1 group-hover/name:text-amber-600 dark:group-hover/name:text-amber-400 transition-colors">
                                                {user?.brand_name || (user ? 'Utilisateur' : 'Invité')}
                                            </h1>
                                            {user?.id && (
                                                <PencilIcon className="w-5 h-5 text-gray-400 opacity-0 group-hover/name:opacity-100 transition-opacity" />
                                            )}
                                        </div>
                                    )}

                                    <p className="text-lg text-gray-500 dark:text-gray-400 font-medium flex items-center justify-center md:justify-start gap-2">
                                        {user?.email}
                                        {user?.is_email_verified && (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-800">
                                                Vérifié
                                            </span>
                                        )}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Account Management */}
                <AccountManagement user={user} />
            </div>
        </div>
    );
}


